<template lang="pug">
#ScannerInStore
  .header-container.d-flex.align-items-center(style="margin-bottom: 0.3rem;")
    b-button.bg-white(
      style="padding: 0.7rem; align-items: center; width: fit-content; display: flex; border-radius: 30px;"
      @click="goBack"
    )
      b-icon(style="color: #181818;" icon="arrow-left")
    span.header-title.pl-2.text-white {{ headerTitle }}

  .camera-select-row
    label.camera-select-label Cámara
    AppSelect(
      v-model="selectedCameraId"
      :options="cameraOptions"
    )
  
  .qr-container(v-if="camerasReady")
    QrScanner(
      :key="scannerKey"
      :camera-id="selectedCameraId"
      fill-container
      @scan="onDetect"
    )
    .qr-container-loader.text-center(v-if="loader")
      b-spinner.float
  b-button.bg-white(v-b-modal.scanned-success-deliveries-modal style="border-radius: 30px; margin-top: 0.3rem; padding: 0.71rem; align-items: center; width: fit-content; display: flex;")
    b-icon(style="padding-top: 0.12rem; color: #181818;" icon="list-ul")
    span.pl-2.code-counter(v-if="scannedCount") {{ scannedCount }}

  b-modal#scanned-success-deliveries-modal(ref="scannedSuccessDeliveries" hide-footer :title="modalTitle" centered)
    scanned-deliveries(
      :deliveries="deliveries"
      @clear="clearDeliveries"
    )
</template>

<script>
import { mapActions } from 'vuex'
import ScannedDeliveries from './ScannedDeliveries.vue'
import QrScanner from '../common/QrScanner.vue'
import AppSelect from '../UI/AppSelect.vue'
import * as Sentry from '@sentry/vue'
import { getLabelCode, getLabelProvider } from '@/utils/scanningUtils'
import { getCameras, pickDefaultCameraId } from '@/utils/cameraDevices'

const CAMERA_STORAGE_KEY = 'scanner_selected_camera_id'

const readStoredCamera = () => {
  const raw = localStorage.getItem(CAMERA_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && parsed.id) {
      return { id: parsed.id, label: parsed.label || null }
    }
  } catch (error) { }
  return { id: raw, label: null }
}

export default {
  components: {
    QrScanner,
    AppSelect,
    ScannedDeliveries
  },
  data() {
    return {
      deliveries: [],
      loader: false,
      success_audio: null,
      error_audio: null,
      scannerKey: '',
      cameras: [],
      camerasReady: false,
      selectedCameraId: readStoredCamera()?.id || null,
      localStorageScanKeys: {
        'cambio-devolucion': 'scan-cambio-devolucion',
        'escaneo-tienda': 'scan-tienda',
        'recepcion-bodega': 'scan-bodega',
        'ruta-conductor': 'scan-ruta-conductor'
      }
    }
  },
  mounted() {
    this.loadDeliveries()
    this.success_audio = new Audio('success_scan.mp3')
    this.error_audio = new Audio('error_scan.wav')
    this.loadCameras()
  },
  computed: {
    scannedCount() {
      return this.deliveries.length
    },
    scanType() {
      const currentRoute = this.$route.path

      switch (currentRoute) {
        case '/cambios-devoluciones':
          return 'cambio-devolucion'
        case '/escaner-en-tienda':
          return 'escaneo-tienda'
        case '/recepcion-en-bodega':
          return 'recepcion-bodega'
        case '/escaner-ruta-conductor':
          return 'ruta-conductor'
        default:
          return null
      }
    },
    modalTitle() {
      const currentRoute = this.$route.path

      switch (currentRoute) {
        case '/cambios-devoluciones':
          return 'Paquetes escaneados para cambio o devolución'
        case '/escaner-en-tienda':
          return 'Paquetes escaneados en tienda'
        case '/recepcion-en-bodega':
          return 'Paquetes escaneados para recepción en bodega'
        case '/escaner-ruta-conductor':
          return 'Paquetes escaneados en ruta conductor'
        default:
          return 'Paquetes escaneados'
      }
    },
    localStorageScanKey() {
      const currentRoute = this.$route.path

      switch (currentRoute) {
        case '/cambios-devoluciones':
          return 'scan-cambio-devolucion'
        case '/escaner-en-tienda':
          return 'scan-tienda'
        case '/recepcion-en-bodega':
          return 'scan-bodega'
        case '/escaner-ruta-conductor':
          return 'scan-ruta-conductor'
        default:
          return 'scan-otros'
      }
    },
    localStorageScanDateKey() {
      const currentRoute = this.$route.path

      switch (currentRoute) {
        case '/cambios-devoluciones':
          return 'scan-cambio-devolucion-date'
        case '/escaner-en-tienda':
          return 'scan-tienda-date'
        case '/recepcion-en-bodega':
          return 'scan-bodega-date'
        case '/escaner-ruta-conductor':
          return 'scan-ruta-conductor-date'
        default:
          return 'scan-otros-date'
      }
    },
    headerTitle() {
      const currentRoute = this.$route.path

      switch (currentRoute) {
        case '/cambios-devoluciones':
          return 'Escaner cambios - devoluciones'
        case '/escaner-en-tienda':
          return 'Escaner en Tienda'
        case '/recepcion-en-bodega':
          return 'Escaner recepción en Bodega'
        case '/escaner-ruta-conductor':
          return 'Escaner ruta conductor'
        default:
          return 'Escaner'
      }
    },
    cameraOptions() {
      return this.cameras.map((cam, idx) => ({
        value: cam.id,
        text: cam.label || `Cámara ${idx + 1}`
      }))
    }
  },
  methods: {
    ...mapActions(['packageScanning', 'listPackage', 'getPackageByLabelCode']),
    async loadCameras() {
      try {
        this.cameras = await getCameras()
        if (this.cameras.length) {
          const stored = readStoredCamera()
          const match = stored
            ? this.cameras.find(c => c.id === stored.id) ||
            (stored.label ? this.cameras.find(c => c.label && c.label === stored.label) : null)
            : null
          this.selectedCameraId = match ? match.id : pickDefaultCameraId(this.cameras)
        }
      } catch (error) {
        console.error('Error loading cameras:', error)
        Sentry.captureException(error, { tags: { context: 'loadCameras' } })
      } finally {
        this.camerasReady = true
      }
    },
    async scanCode(code) {
      try {
        await this.packageScanning({ codes: [code], scan_type: this.scanType })
        this.$root.$bvToast.toast(`El paquete ${code} fue actualizado con éxito!`, {
          title: 'Éxito',
          variant: 'success',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 7000,
          solid: true
        })
      } catch (err) {
        this.$root.$bvToast.toast(err.response.data.message || 'No se pudo marcar el paquete', {
          title: 'Error',
          variant: 'danger'
        })
      }
    },
    clearDeliveries() {
      this.deliveries = []
      localStorage.setItem(this.localStorageScanKey, JSON.stringify([]))
      localStorage.setItem(this.localStorageScanDateKey, this.$moment.tz(new Date(), 'America/Santiago').format('YYYY-MM-DD'))
      console.log('Deliveries cleared')
    },
    loadDeliveries() {
      const currentDate = this.$moment.tz(new Date(), 'America/Santiago').format('YYYY-MM-DD')
      const storedDate = localStorage.getItem(this.localStorageScanDateKey)
      if (currentDate !== storedDate) {
        this.clearDeliveries()
      }
      this.deliveries = JSON.parse(localStorage.getItem(this.localStorageScanKey)) || []
      console.log('Deliveries loaded', this.deliveries)
    },
    goBack() {
      this.$router.push('/mis-envios')
    },
    async onDetect(content) {
      try {
        this.loader = true
        const labelCode = getLabelCode(content)
        console.log('Label Code', labelCode)
        const labelProvider = getLabelProvider(content)
        console.log('Label Provider', labelProvider)

        const response = await this.getPackageByLabelCode({ labelCode, labelProvider })
        const pkg = response.data
        const duplicated = this.deliveries.filter(d => d.code === pkg.code)

        if (duplicated.length) {
          await this.error_audio.play()
          this.loader = false
          return this.$bvToast.toast(`El paquete ${labelCode} ya fue escaneado`, {
            title: 'Error',
            variant: 'danger',
            toaster: 'b-toaster-top-center',
            autoHideDelay: 7000,
            solid: true
          })
        }
        await this.success_audio.play()
        await this.scanCode(pkg.code)
        this.deliveries.push({ code: pkg.code, commune: pkg.commune?.name || 'No asignada', address: pkg.customerAddress, customerName: pkg.customerName, store: pkg.withdrawal.store })
        localStorage.setItem(this.localStorageScanKey, JSON.stringify(this.deliveries))
        localStorage.setItem(this.localStorageScanDateKey, this.$moment.tz(new Date(), 'America/Santiago').format('YYYY-MM-DD'))
      } catch (error) {
        await this.error_audio.play()
        const message = error.response?.data?.message || 'Ocurrió un error al escanear el paquete'
        return this.$bvToast.toast(message, {
          title: 'Error',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 7000,
          solid: true
        })
      } finally {
        this.loader = false
      }
    },
    clearDeliveriesByType(scanType) {
      console.log('Clearing deliveries by type', scanType)
      localStorage.removeItem(this.localStorageScanKeys[scanType])
    },
    clearAll() {
      console.log('Clearing all')
      Object.keys(this.localStorageScanKeys).forEach(key => {
        this.clearDeliveriesByType(key)
      })
    }
  },
  watch: {
    selectedCameraId(newId) {
      if (newId && typeof newId === 'string') {
        const cam = this.cameras.find(c => c.id === newId)
        const stored = { id: newId, label: cam ? cam.label : null }
        localStorage.setItem(CAMERA_STORAGE_KEY, JSON.stringify(stored))
      }
    },
    '$route.path': {
      handler() {
        this.clearAll()
        this.loadDeliveries()
        this.scannerKey = Date.now()
      }
    },
  },
  destroyed() {
    this.clearAll()
  }
}
</script>

<style lang="scss" scoped>
#ScannerInStore {
  background-color: #191919;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .header-container,
  > .btn {
    flex: 0 0 auto;
  }

  .qr-container {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0.4rem 0rem;
    position: relative;
  }

  .qr-container-loader {
    align-items: center;
    display: flex;
    justify-content: center;
    position: absolute;
    inset: 0;
    z-index: 1;
  }
}
</style>
<style lang="scss" scoped>
.camera-select-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  background-color: #232323;
}

.camera-select-label {
  color: #ccc;
  font-size: 0.85rem;
  white-space: nowrap;
  margin: 0;
}
</style>
<style lang="scss">
#scanned-success-deliveries-modal {
  .modal-dialog-centered {
    align-items: end !important;
  }

  .modal-content {
    border-radius: 16px 16px 0 0 !important;
    transition-timing-function: ease-out !important;
    transition-duration: 300ms !important;

    .modal-body {
      padding: 0.7rem 0.4rem;
      padding-bottom: 0;
    }

    .table th,
    .table td {
      padding: 0.4rem;
      vertical-align: middle;
      font-size: 15px;
      font-weight: 400;
    }
  }
}

.code-counter {
  color: #13475c;
}
</style>
